// filepath: apps/web/src/operator/SummaryTable.tsx
//
// FR-UI-OPS-1 AC-3 (d) / FR-UI-OPS-3 — per-tenant breakdown table.
//
// Columns:
//   - slug                 (string, plain text)
//   - instance_count       (numeric, sortable)
//   - error_count          (numeric, sortable, rendered as Badge)
//   - active_sandbox_count (numeric, sortable)
//
// Sortable by all 3 numeric columns via column-header click
// handlers. The first click sorts ascending; the next click
// toggles to descending. The sort indicator is rendered as
// `▲` / `▼` glyphs (matching the existing FR-UI-LIST-1 chrome
// style — see `apps/web/src/i18n/i18n.ts` `lists.sort.*`).
//
// EMPTY STATE: when `tenants.length === 0`, the table is
// REPLACED with the i18n key `operator.summary.empty` ("No
// tenants yet"). NOT a blank table — explicitly surfaced so a
// future regression that breaks the empty path is loud-failed
// by the test in `summaryTable.test.tsx`.
//
// FR-UI-OPS-3 redesign: per-row chrome now lives inside a
// `Card` primitive; the error-count cell uses a `Badge` whose
// `tone` is derived from the per-tenant error rate
// (`>0` → danger, `===0` → neutral) — matches the design
// composition. All test ids are preserved verbatim.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { Badge, type BadgeTone, Button, Card } from "@/design-system";
import type { SummaryTenantRow } from "./useOperatorSummary";

export interface SummaryTableProps {
  tenants: SummaryTenantRow[];
  /**
   * FR-UI-OPS-2 AC-3 — optional row-click handler. When provided,
   * each tenant slug renders as a keyboard-focusable button that
   * opens the per-tenant drill-down. When omitted, the slug renders
   * as plain text (backward compatible with existing callers / tests).
   */
  onRowClick?: (row: SummaryTenantRow) => void;
}

type SortColumn = "instanceCount" | "errorCount" | "activeSandboxCount";
type SortDirection = "asc" | "desc";

const NUMBER_FIELDS: ReadonlyArray<SortColumn> = [
  "instanceCount",
  "errorCount",
  "activeSandboxCount",
];

/**
 * Derive a semantic badge tone for the per-tenant error count.
 * `>0` errors → `danger`; `===0` → `neutral`. (Future: `warning`
 * when error_rate crosses a threshold, but the v1 summary payload
 * does not carry a per-tenant error rate — we use raw count.)
 */
function errorToneFor(errorCount: number, instanceCount: number): BadgeTone {
  if (errorCount <= 0) return "neutral";
  // Any active failure is treated as danger in v1; a future
  // FR can refine to a `warning` band by error rate.
  if (instanceCount > 0) return "danger";
  // No instances to attribute errors against — degrade to warning.
  return "warning";
}

/**
 * SummaryTable — pure presentation. Sort state is held locally
 * (no parent coupling required).
 */
export function SummaryTable({
  tenants,
  onRowClick,
}: SummaryTableProps): React.ReactElement {
  const { t } = useTranslation();
  const [sortColumn, setSortColumn] = React.useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] =
    React.useState<SortDirection>("asc");

  const sorted = React.useMemo<SummaryTenantRow[]>(() => {
    if (sortColumn === null) return tenants;
    const copy = tenants.slice();
    copy.sort((a, b) => {
      const av = a[sortColumn];
      const bv = b[sortColumn];
      const cmp = av - bv;
      return sortDirection === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [tenants, sortColumn, sortDirection]);

  const onHeaderClick = (column: SortColumn): void => {
    if (sortColumn === column) {
      // Toggle direction.
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const renderSortGlyph = (column: SortColumn): string => {
    if (sortColumn !== column) return "";
    return sortDirection === "asc"
      ? t("lists.sort.asc", "▲")
      : t("lists.sort.desc", "▼");
  };

  if (tenants.length === 0) {
    return (
      <Card padding="lg" data-testid="operator-summary-table-empty-card">
        <p
          data-testid="operator-summary-table-empty"
          className="operator-summary-table-empty"
          role="status"
          aria-live="polite"
          style={{
            margin: 0,
            fontSize: "var(--text-base)",
            color: "var(--text-body)",
            textAlign: "center",
          }}
        >
          {t("operator.summary.empty", "No tenants yet")}
        </p>
      </Card>
    );
  }

  const ariaLabel = t(
    "operator.summary.table.aria_label",
    "Per-tenant breakdown",
  );

  const headerButton = (
    column: SortColumn,
    label: string,
    testId: string,
  ): React.ReactElement => (
    <button
      type="button"
      onClick={() => onHeaderClick(column)}
      data-testid={`operator-summary-table-sort-${testId}`}
      aria-label={`${label} — sort`}
      className="operator-summary-table-sort-button"
      style={{
        background: "transparent",
        border: "none",
        padding: 0,
        font: "inherit",
        color: "var(--text-secondary)",
        fontWeight: "var(--font-semibold)",
        cursor: "pointer",
        textAlign: "right",
      }}
    >
      {label} {renderSortGlyph(column)}
    </button>
  );

  return (
    <Card padding="sm" data-testid="operator-summary-table-card">
      <table
        data-testid="operator-summary-table"
        className="operator-summary-table"
        aria-label={ariaLabel}
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontFamily: "var(--font-sans)",
        }}
      >
        <caption className="visually-hidden">{ariaLabel}</caption>
        <thead>
          <tr>
            <th
              scope="col"
              style={{
                textAlign: "left",
                padding: "var(--space-3) var(--space-4)",
                fontSize: "var(--text-xs)",
                fontWeight: "var(--font-semibold)",
                color: "var(--text-secondary)",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                borderBottom: "1px solid var(--border-default)",
              }}
            >
              {t("operator.summary.table.tenant", "Tenant")}
            </th>
            <th
              scope="col"
              style={{
                textAlign: "right",
                padding: "var(--space-3) var(--space-4)",
                borderBottom: "1px solid var(--border-default)",
              }}
            >
              {headerButton(
                "instanceCount",
                t("operator.summary.table.instanceCount", "Active instances"),
                "instance-count",
              )}
            </th>
            <th
              scope="col"
              style={{
                textAlign: "right",
                padding: "var(--space-3) var(--space-4)",
                borderBottom: "1px solid var(--border-default)",
              }}
            >
              {headerButton(
                "errorCount",
                t("operator.summary.table.errorCount", "Errors"),
                "error-count",
              )}
            </th>
            <th
              scope="col"
              style={{
                textAlign: "right",
                padding: "var(--space-3) var(--space-4)",
                borderBottom: "1px solid var(--border-default)",
              }}
            >
              {headerButton(
                "activeSandboxCount",
                t(
                  "operator.summary.table.activeSandboxCount",
                  "Active sandboxes",
                ),
                "active-sandbox-count",
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const tone = errorToneFor(row.errorCount, row.instanceCount);
            return (
              <tr
                key={row.tenantId}
                data-testid={`operator-summary-table-row-${row.slug}`}
                style={{
                  borderBottom: "1px solid var(--border-default)",
                }}
              >
                <td
                  style={{
                    padding: "var(--space-3) var(--space-4)",
                    fontWeight: "var(--font-semibold)",
                    color: "var(--text-primary)",
                  }}
                >
                  {onRowClick ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRowClick(row)}
                      data-testid={`operator-summary-table-drilldown-${row.slug}`}
                      aria-label={t(
                        "operator.drillDown.openFor",
                        "Open details for tenant {{slug}}",
                        { slug: row.slug },
                      )}
                      style={{
                        padding: "4px 8px",
                        fontFamily: "var(--font-sans)",
                      }}
                    >
                      <code>{row.slug}</code>
                    </Button>
                  ) : (
                    <code>{row.slug}</code>
                  )}
                </td>
                <td
                  style={{
                    padding: "var(--space-3) var(--space-4)",
                    textAlign: "right",
                    color: "var(--text-secondary)",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {row.instanceCount}
                </td>
                <td
                  style={{
                    padding: "var(--space-3) var(--space-4)",
                    textAlign: "right",
                  }}
                >
                  <Badge
                    tone={tone}
                    data-testid={`operator-summary-table-error-${row.slug}`}
                  >
                    {row.errorCount} {t("operator.summary.errorUnit", "err")}
                  </Badge>
                </td>
                <td
                  style={{
                    padding: "var(--space-3) var(--space-4)",
                    textAlign: "right",
                    color: "var(--text-secondary)",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {row.activeSandboxCount}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

/**
 * Re-export the numeric column list for tests that verify the
 * "sortable by all 3 numeric columns" claim. (slug is the
 * string column; not exposed as a SortColumn here since the
 * AC requires the 3 numeric columns to be sortable.)
 */
export const SUMMARY_TABLE_SORT_COLUMNS = NUMBER_FIELDS;
